import type { AudienceDefinition, AudienceFilters, CreateAudienceRequest } from '../types';
import { DEFAULT_BRAND_SHARE_PERCENT } from './brandShare';
import { DEFAULT_PURCHASE_LOOKBACK_DAYS, FILTER_KEYS, pickFilters } from './filters';

/**
 * How far before the submit a saved definition's `createdAt` may fall and still count as that submit's row.
 *
 * `createdAt` is the service's clock and the submit time is the browser's, so the allowance absorbs clock skew between them.
 */
export const UNCONFIRMED_CREATE_CLOCK_SKEW_MS = 30_000;

/**
 * Whether `definition` is the row a create with `request`, submitted at `submittedAt` (epoch ms), may have saved.
 *
 * Names are not unique in proxima-audiences, so a match needs all of these:
 * - the same trimmed name;
 * - the same description, with blank and missing read as null;
 * - the same filter set, compared after normalizing;
 * - the same brand share, with a missing one read as `DEFAULT_BRAND_SHARE_PERCENT`;
 * - a `createdAt` no earlier than `submittedAt` minus `UNCONFIRMED_CREATE_CLOCK_SKEW_MS`;
 * - a `createdByUserId` equal to `currentUserId`, checked only when both are known.
 *
 * The creator check keeps a teammate's identical audience from reading as this user's save.
 */
export const isUnconfirmedCreateMatch = (
  definition: AudienceDefinition,
  request: CreateAudienceRequest,
  submittedAt: number,
  currentUserId: string | null
): boolean =>
  definition.name.trim() === request.name.trim() &&
  (definition.createdByUserId == null ||
    currentUserId == null ||
    definition.createdByUserId === currentUserId) &&
  normalizedText(definition.description) === normalizedText(request.description) &&
  definition.brandSharePercent === (request.brandSharePercent ?? DEFAULT_BRAND_SHARE_PERCENT) &&
  Date.parse(definition.createdAt) >= submittedAt - UNCONFIRMED_CREATE_CLOCK_SKEW_MS &&
  JSON.stringify(normalizedFilters(definition)) === JSON.stringify(normalizedFilters(request));

/* Sorted arrays, nulls for absent scalars, and the default purchase window the service stores for a null one */
const normalizedFilters = (source: Partial<AudienceFilters> | AudienceDefinition) => {
  const filters = pickFilters(source);
  return FILTER_KEYS.map(key => {
    const value = filters[key];
    if (Array.isArray(value)) return [...value].sort();
    if (key === 'purchaseLookbackDays') return value ?? DEFAULT_PURCHASE_LOOKBACK_DAYS;
    return value ?? null;
  });
};

const normalizedText = (text: string | null | undefined): string | null => text?.trim() || null;
